import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import {
  datadogApiRef,
  IncidentsResponse,
  ServiceHealthResponse,
  SlosResponse,
} from '../api';
import {
  getServiceName,
  getSloIds,
  isDatadogAvailable,
} from '../annotations';

type AsyncState<T> = {
  loading: boolean;
  error?: Error | undefined;
  value?: T;
};

export type DatadogData = {
  monitors: ServiceHealthResponse | null;
  slos: SlosResponse | null;
  incidents: IncidentsResponse | null;
};

const DatadogDataContext = createContext<AsyncState<DatadogData | null> | null>(
  null,
);

export const DatadogDataProvider = ({ children }: { children: ReactNode }) => {
  const { entity } = useEntity();
  const datadogApi = useApi(datadogApiRef);

  const available = isDatadogAvailable(entity);
  const serviceName = getServiceName(entity);
  const sloIds = getSloIds(entity);
  const sloIdsKey = sloIds?.join(',') ?? '';

  const state = useAsync(async () => {
    if (!available) return null;
    const [monitors, slos, incidents] = await Promise.all([
      serviceName
        ? datadogApi.getMonitors(serviceName).catch(() => null)
        : Promise.resolve(null),
      serviceName || sloIds
        ? datadogApi.getSlos(serviceName ?? '', sloIds).catch(() => null)
        : Promise.resolve(null),
      serviceName
        ? datadogApi.getIncidents(serviceName).catch(() => null)
        : Promise.resolve(null),
    ]);
    return { monitors, slos, incidents };
  }, [available, serviceName, sloIdsKey]);

  return (
    <DatadogDataContext.Provider value={state}>
      {children}
    </DatadogDataContext.Provider>
  );
};

// Returns the shared unfiltered fetch (monitors+slos+incidents) or null if
// rendered outside a DatadogDataProvider. Components should typically use the
// per-slice hooks below.
const useSharedDatadogData = () => useContext(DatadogDataContext);

export const useDatadogMonitors = (): AsyncState<ServiceHealthResponse | null> => {
  const shared = useSharedDatadogData();
  const { entity } = useEntity();
  const datadogApi = useApi(datadogApiRef);
  const serviceName = getServiceName(entity);

  const useLocal = !shared;

  const local = useAsync(async () => {
    if (!useLocal || !serviceName) return null;
    return datadogApi.getMonitors(serviceName);
  }, [useLocal, serviceName]);

  return useMemo(() => {
    if (useLocal) return local;
    return {
      loading: shared!.loading,
      error: shared!.error,
      value: shared!.value?.monitors ?? null,
    };
  }, [useLocal, local, shared]);
};

export const useDatadogSlos = (): AsyncState<SlosResponse | null> => {
  const shared = useSharedDatadogData();
  const { entity } = useEntity();
  const datadogApi = useApi(datadogApiRef);
  const serviceName = getServiceName(entity);
  const sloIds = getSloIds(entity);
  const sloIdsKey = sloIds?.join(',') ?? '';

  const useLocal = !shared;

  const local = useAsync(async () => {
    if (!useLocal) return null;
    if (!serviceName && !sloIds) return null;
    return datadogApi.getSlos(serviceName ?? '', sloIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLocal, serviceName, sloIdsKey]);

  return useMemo(() => {
    if (useLocal) return local;
    return {
      loading: shared!.loading,
      error: shared!.error,
      value: shared!.value?.slos ?? null,
    };
  }, [useLocal, local, shared]);
};

export const useDatadogIncidents = (): AsyncState<IncidentsResponse | null> => {
  const shared = useSharedDatadogData();
  const { entity } = useEntity();
  const datadogApi = useApi(datadogApiRef);
  const serviceName = getServiceName(entity);

  const useLocal = !shared;

  const local = useAsync(async () => {
    if (!useLocal || !serviceName) return null;
    return datadogApi.getIncidents(serviceName);
  }, [useLocal, serviceName]);

  return useMemo(() => {
    if (useLocal) return local;
    return {
      loading: shared!.loading,
      error: shared!.error,
      value: shared!.value?.incidents ?? null,
    };
  }, [useLocal, local, shared]);
};
